// src/api/ProductMatchCandidates.ts
import Requester from "./Requester";

export type PMCStatus = 'pending' | 'approved' | 'rejected' | 'ignored';

export interface PMCSignals {
    // что угодно, что пишешь в signals (ключ-значение)
    [k: string]: unknown;
}

export interface ProductLite {
    id: number;
    name: string;
    sku: string;
    brand_id?: number | null;
}

export interface ProductMatchCandidate {
    id: number;
    product_a_id: number;
    product_b_id: number;
    brand: string | null;
    bucket_key: string | null;
    score: number;
    status: PMCStatus | null;
    suggested_master_id: number | null;
    notes?: string | null;
    signals?: PMCSignals | null;

    // если бэкенд грузит связи через with()
    productA?: ProductLite;
    productB?: ProductLite;
    suggestedMaster?: ProductLite | null;
}

export interface IndexFilters {
    brand?: string;
    status?: PMCStatus | null;
    min_score?: number;
    max_score?: number;
    product_id?: number;
    bucket?: string;
    only_pending?: boolean;
}

export interface Page<T> {
    data: T[];
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
}

type ReqOptions = { signal?: AbortSignal } | undefined;

function toQueryArray(obj: Record<string, any>) {
    return Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([name, value]) => ({ name, value }));
}

// LIST
export function getCandidates(
    { page = 1, perPage = 50, filters = {} as IndexFilters } = {},
    options?: ReqOptions
) {
    const requester = new Requester();
    const query = [
        ...toQueryArray(filters),
        { name: 'per_page', value: perPage },
        { name: 'page', value: page },
    ];

    return requester.get(
        requester.getUrl('api/product-match-candidates', query),
        { signal: options?.signal }
    );
}

// SHOW
export function getCandidate(id: number, options?: ReqOptions) {
    const requester = new Requester();
    return requester.get<ProductMatchCandidate>(
        requester.getUrl(`api/product-match-candidates/${id}`),
        { signal: options?.signal }
    );
}

// UPDATE (частичный)
export function updateCandidate(
    id: number,
    payload: Partial<Pick<ProductMatchCandidate, 'status' | 'suggested_master_id' | 'notes' | 'score' | 'signals'>>,
    options?: ReqOptions
) {
    const requester = new Requester();
    return requester.post<{ ok: boolean; item: ProductMatchCandidate }>(
        requester.getUrl(`api/product-match-candidates/${id}`),
        payload,
        { signal: options?.signal }
    );
}

// APPROVE
export function approveCandidate(
    id: number,
    params: { master_id?: number; notes?: string } = {},
    options?: ReqOptions
) {
    const requester = new Requester();
    return requester.post<{ ok: boolean; item: ProductMatchCandidate }>(
        requester.getUrl(`api/product-match-candidates/${id}/approve`),
        params,
        { signal: options?.signal }
    );
}

// REJECT
export function rejectCandidate(
    id: number,
    params: { notes?: string } = {},
    options?: ReqOptions
) {
    const requester = new Requester();
    return requester.post<{ ok: boolean; item: ProductMatchCandidate }>(
        requester.getUrl(`api/product-match-candidates/${id}/reject`),
        params,
        { signal: options?.signal }
    );
}

// BULK
export function bulkUpdate(
    ids: number[],
    action: PMCStatus | 'ignore' | 'pending',
    options?: ReqOptions
) {
    const requester = new Requester();
    return requester.post<{ ok: boolean; updated: number }>(
        requester.getUrl('api/product-match-candidates/bulk'),
        { ids, action },
        { signal: options?.signal }
    );
}

// DESTROY
export function destroyCandidate(id: number, options?: ReqOptions) {
    const requester = new Requester();
    return requester.delete<{ ok: boolean }>(
        requester.getUrl(`api/product-match-candidates/${id}`),
        { signal: options?.signal }
    );
}

export default {
    getCandidates,
    getCandidate,
    updateCandidate,
    approveCandidate,
    rejectCandidate,
    bulkUpdate,
    destroyCandidate,
};